import { api } from "@lib/orpc-client";
import { SupportedBank } from "./useSupportedBank";
import { useCallback, useEffect, useState } from "react";
import {
  AddBankParams,
  EditBankParams,
  RedemptionOption,
} from "../types/redemption";
import * as Sentry from "@sentry/react";

type BankAccount = Awaited<ReturnType<typeof api.wallet.banking.list>>[number];

const useBankManagement = (fetchBanks: boolean = true) => {
  const addBank = async ({
    account_nickname,
    asset = "idr",
    source = "REDEMPTION",
    bank_name,
    bank_account,
  }: AddBankParams) => {
    try {
      return await api.walletV2.banking.add({
        bank_name,
        bank_account,
        account_nickname,
        asset,
        source,
      });
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  };

  const editBank = async ({
    bank_uuid,
    account_nickname,
    asset = "idr",
    source = "REDEMPTION",
    bank_name,
    bank_account,
  }: EditBankParams) => {
    try {
      return await api.walletV2.banking.update({
        bank_uuid,
        account_nickname,
        bank_name,
        bank_account,
        asset,
        source,
      });
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  };

  const removeBank = async ({ bank_uuid }: { bank_uuid: string }) => {
    try {
      await api.wallet.banking.delete({ bank_uuid });
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  };

  const validateBank = async ({ bank_uuid }: { bank_uuid: string }) => {
    try {
      return await api.walletV2.banking.validation({ bank_uuid });
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  };

  const [banks, setBanks] = useState<BankAccount[]>([]);
  const [bankAsRedemptionOption, setBankAsRedemptionOption] = useState<
    RedemptionOption[]
  >([]);
  const [supportedBanks, setSupportedBanks] = useState<SupportedBank[]>([]);
  const [localLoading, setLocalLoading] = useState(true);

  const getDataBase = async () => {
    try {
      setLocalLoading(true);
      const banks = await api.wallet.banking.list({});
      const supportedBanks = await api.walletV2.banking.supportedBanks({});
      const bankAsRedemptionOption: RedemptionOption[] = banks.map((bank) => {
        return {
          id: bank.bank_uuid,
          name: bank.bank_account_name,
          iconUrl:
            supportedBanks?.find(
              (supportedBank) => supportedBank.code === bank.bank_name
            )?.icon_url || "",
          accountNumber: bank.bank_account,
          provider:
            supportedBanks?.find(
              (supportedBank) => supportedBank.code === bank.bank_name
            )?.display_name || "",
          nickname: bank.account_nickname,
          isValid: bank.status === "VALIDATED",
        };
      });
      setSupportedBanks(supportedBanks);
      setBanks(banks);
      setBankAsRedemptionOption(bankAsRedemptionOption);
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setLocalLoading(false);
    }
  };

  const getData = useCallback(() => {
    getDataBase();
  }, []);

  useEffect(() => {
    if (!fetchBanks) return;
    getData();
  }, [fetchBanks]);

  return {
    addBank,
    editBank,
    removeBank,
    validateBank,
    banks,
    bankAsRedemptionOption,
    supportedBanks,
    isLoading: localLoading,
    reload: getDataBase,
  };
};

export default useBankManagement;
